// Per-rail deposit fee model. ONE source for the number the user is shown and the number
// the server charges.
//
// SAB-1095: it is now literally one source. This module and paypal-create-order both call
// buildDeposit from supabase/functions/_shared/fees.ts — the frontend reaches it through the
// `@shared` alias that SAB-582 added to vite.config.ts, tsconfig.app.json and
// vitest.config.ts for exactly this purpose.
//
// Before this, both sides carried their own copy of `PCT = 0.029, FIXED = 0.30`. The rate
// was wrong (PayPal's real US rate is 3.49% + $0.49, and _shared/fees.ts already held it as
// a CONFIRMED schedule), so every quote under-collected by ~0.8%. Two copies are what made
// that possible, and a third test file restating the same numbers could only ever prove the
// copies matched each other — never that they matched PayPal. Conformance is asserted
// against real observed fees in depositFees.test.ts instead.
//
// THE TRAP this still guards: PayPal's percentage is charged on the GROSS, so `amount * rate`
// under-collects and the user would be credited less than they asked for.

import { buildDeposit } from '@shared/fees';

export type DepositRail = 'card' | 'paypal' | 'venmo';

export interface FeeBreakdown {
  /** What the user asked for, and what lands in their balance. */
  credited: number;
  /** What the processor charges them. Equals `credited` when there is no fee. */
  charged: number;
  /** charged - credited. Zero when there is no fee. */
  fee: number;
  /** False for rails we do not surface a fee for (debit card). */
  hasFee: boolean;
}

/**
 * Soap debit carries no processor fee we pass on, so nothing is shown — an explicit
 * "$0.00 fee" row reads as a fee the user should look for. PayPal and Venmo ride the same
 * PayPal rails and the same pricing.
 */
export function feeFor(rail: DepositRail, amount: number): FeeBreakdown {
  if (!Number.isFinite(amount) || amount <= 0) {
    return { credited: 0, charged: 0, fee: 0, hasFee: rail !== 'card' };
  }
  if (rail === 'card') {
    return { credited: amount, charged: amount, fee: 0, hasFee: false };
  }
  // Integer cents end to end — the same call the server makes, so the quote cannot drift
  // from the charge by construction rather than by two files agreeing to be careful.
  const d = buildDeposit(Math.round(amount * 100), 'paypal');
  return {
    credited: d.amountCents / 100,
    charged: d.grossCents / 100,
    fee: (d.grossCents - d.amountCents) / 100,
    hasFee: true,
  };
}

/** "Deposit $100 → charged $104.12 ($4.12 fee) → $100 to your balance." */
export function feeSentence(rail: DepositRail, amount: number): string | null {
  const b = feeFor(rail, amount);
  if (!b.hasFee || b.credited <= 0) return null;
  const usd = (n: number) => `$${n.toFixed(2)}`;
  return `You'll be charged ${usd(b.charged)} (${usd(b.fee)} processor fee) so ${usd(b.credited)} lands in your balance.`;
}
